import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react'

import { SuppliersBalance } from '../../../../../types/SuppliersBalance'

interface Params {
  /** The currently visible (filtered) suppliers, in render order. */
  suppliers: SuppliersBalance[]
  /** Index of the supplier matching the active route (falls back to 0). */
  selectedIndex: number
  /** Current search query — focus resets to the selection when it changes. */
  searchQuery?: string
  /** Skips auto-focus while the list is still loading skeletons. */
  isLoading: boolean
  /** Navigates to a supplier; `replace` avoids polluting history during arrow nav. */
  onNavigate: (supplier: SuppliersBalance, options: { replace: boolean }) => void
  /** Returns focus to the search field when the user navigates up past the first result. */
  onReturnToSearch: () => void
}

interface Result {
  /** Index of the roving-tabindex item (the one that is keyboard-reachable). */
  focusedIndex: number
  /** Ref callback factory to register each list item button by index. */
  registerItemRef: (index: number) => (el: HTMLButtonElement | null) => void
  /** ArrowUp/ArrowDown handler to attach to the list container. */
  handleKeyDown: (event: KeyboardEvent<HTMLUListElement>) => void
  /** ArrowDown handler for the search field — moves focus into the result list. */
  handleSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void
}

/**
 * Keyboard navigation + focus management for the supplier list sidebar.
 *
 * Implements a roving tabindex over the filtered suppliers: ArrowUp/ArrowDown
 * move the focused item (and navigate to it via `replace`), and the currently
 * selected supplier is auto-focused once on mount so the keyboard works without
 * first clicking into the list.
 *
 * The search field and the list form a keyboard round-trip: ArrowDown from the
 * search field moves into the list, and ArrowUp from the first result returns to
 * the search field, so the user is never trapped in the results.
 */
export const useSupplierListKeyboardNav = ({
  suppliers,
  selectedIndex,
  searchQuery,
  isLoading,
  onNavigate,
  onReturnToSearch,
}: Params): Result => {
  const [focusedIndex, setFocusedIndex] = useState(selectedIndex)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const hasAutoFocusedRef = useRef(false)

  useEffect(() => {
    setFocusedIndex(selectedIndex)
  }, [selectedIndex, searchQuery])

  useEffect(() => {
    if (hasAutoFocusedRef.current || isLoading || suppliers.length === 0) {
      return
    }
    const selectedButton = itemRefs.current[selectedIndex]
    if (selectedButton) {
      hasAutoFocusedRef.current = true
      selectedButton.focus()
      // Optional chain: jsdom (test env) doesn't implement scrollIntoView.
      selectedButton.scrollIntoView?.({ block: 'nearest' })
    }
  }, [isLoading, suppliers, selectedIndex])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
        return
      }

      event.preventDefault()

      // Returning to the search field keeps the user from being trapped at the top.
      if (event.key === 'ArrowUp' && focusedIndex === 0) {
        onReturnToSearch()
        return
      }

      const next =
        event.key === 'ArrowDown' ? Math.min(focusedIndex + 1, suppliers.length - 1) : Math.max(focusedIndex - 1, 0)

      if (next === focusedIndex) {
        return
      }

      setFocusedIndex(next)
      onNavigate(suppliers[next], { replace: true })

      const nextButton = itemRefs.current[next]
      if (nextButton) {
        nextButton.focus()
        // Optional chain: jsdom (test env) doesn't implement scrollIntoView.
        nextButton.scrollIntoView?.({ block: 'nearest' })
      }
    },
    [focusedIndex, suppliers, onNavigate, onReturnToSearch],
  )

  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'ArrowDown' || suppliers.length === 0) {
        return
      }

      event.preventDefault()

      // Select the result as we enter the list so a single press both focuses
      // and selects it — otherwise the first press would only move focus.
      onNavigate(suppliers[focusedIndex], { replace: true })

      const targetButton = itemRefs.current[focusedIndex]
      if (targetButton) {
        targetButton.focus()
        // Optional chain: jsdom (test env) doesn't implement scrollIntoView.
        targetButton.scrollIntoView?.({ block: 'nearest' })
      }
    },
    [focusedIndex, suppliers, onNavigate],
  )

  const registerItemRef = useCallback(
    (index: number) => (el: HTMLButtonElement | null) => {
      itemRefs.current[index] = el
    },
    [],
  )

  return { focusedIndex, registerItemRef, handleKeyDown, handleSearchKeyDown }
}
